import { CHAT_TAKEOVER_CLASSNAME } from 'constants/frames'
import { triggerCampaignActionAPIEvent } from 'modules/api/events'
import { ActiveCampaign } from 'modules/campaigns/types'
import { createConversationEvent } from 'modules/conversations'
import { toggleChat } from 'modules/view/actions'
import { pathOr } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConversationEventType, ConversationId } from 'types/conversation'
import { broadcast } from 'utils/message'

import useActiveCampaign from './useActiveCampaign'

const createCampaignDismissedEvent = (conversationId: ConversationId, campaign: ActiveCampaign) => {
  triggerCampaignActionAPIEvent(ConversationEventType.CLICKED, campaign.id)

  if (typeof conversationId !== 'number') {
    return null
  }

  return createConversationEvent(ConversationEventType.DISMISSED, {
    conversationId: conversationId as number
  })
}

const useCloseChat = () => {
  const activeCampaign = useActiveCampaign()
  const endUser = useSelector(state => state.session.endUser)
  const dispatch = useDispatch()
  const [isClosed, setIsClosed] = useState(false)

  const isChatTakeover = useSelector(state => state.view.isChatTakeover)
  const closeChatRedirectUrl = useSelector(state => (
    pathOr(null, ['view', 'closeChatRedirectUrl'], state))
  )

  const activeConversationId: ConversationId = useSelector(state =>
    state.conversations.activeConversation
  )

  const endUserResponded = useSelector(state =>
    pathOr(false, ['conversations', 'conversationStatus', activeConversationId, 'endUserResponded'], state)
  )

  useEffect(() => {
    if (!isClosed) {
      return
    }

    dispatch(toggleChat({
      chatOpen: false,
      isChatTakeover: false
    }))

    setIsClosed(false)
  }, [isClosed, dispatch])

  const closeChatTakeover = useCallback(() => {
    broadcast({
      topic: 'remove-chat-takeover',
      message: {
        classNamesToRemove: [CHAT_TAKEOVER_CLASSNAME],
      }
    })

    let promise: Promise<any>

    if (!endUserResponded) {
      const promises = [
        createCampaignDismissedEvent(activeConversationId, activeCampaign),
      ]

      if (activeCampaign && activeCampaign.id) {
        const attributes: Record<string, number> = {
          campaignId: activeCampaign.id,
          endUserId: endUser.id
        }

        if (activeCampaign.attributes.playbookId) {
          attributes.playbookId = activeCampaign.attributes.playbookId
        }

        promises.push(broadcast({
          topic: 'send-usage-metric-event',
          message: {
            name: 'Campaign Dismissed',
            attributes,
            publishImmediately: true
          }
        }))
      }

      promise = Promise.all(promises)
    }

    if (closeChatRedirectUrl) {
      const redirect = () => broadcast({
        topic: 'redirect-to-url',
        message: {
          url: closeChatRedirectUrl
        }
      })

      if (promise) {
        promise.then(redirect)
      } else {
        redirect()
      }
    }
  }, [activeCampaign, activeConversationId, closeChatRedirectUrl, endUser.id, endUserResponded])

  return useCallback(function closeChat() {
    setIsClosed(true)

    if (isChatTakeover) {
      closeChatTakeover()
    }
  }, [isChatTakeover, closeChatTakeover])
}

export default useCloseChat
